import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { schoolsApi } from '@/api/endpoints/schools';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { formatDate } from '@/utils/format';

interface SchoolsPageProps {
  readOnly?: boolean;
}

export const SchoolsPage = ({ readOnly = false }: SchoolsPageProps) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const stageFilter = readOnly ? 2 : undefined;
  const { data, isLoading } = useQuery({
    queryKey: ['schools', { page, stage: stageFilter }],
    queryFn: () => schoolsApi.getAll({ page, limit: 10, stage: stageFilter }),
  });

  const { data: schoolDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['school', expandedId],
    queryFn: () => schoolsApi.getById(expandedId!),
    enabled: !!expandedId,
  });

  const deleteMutation = useMutation({
    mutationFn: schoolsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schools'] });
      queryClient.invalidateQueries({ queryKey: ['import-stats'] });
      toast.success('Szkoła została usunięta');
    },
    onError: () => toast.error('Nie udało się usunąć szkoły'),
  });

  const exportStudentsMutation = useMutation({
    mutationFn: () => schoolsApi.exportStudentsCsv(),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'uczestnicy.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: () => toast.error('Nie udało się wyeksportować listy uczestników'),
  });

  if (isLoading) return <LoadingSpinner size="lg" className="mt-20" />;

  const schools = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Szkoły</h2>
          <p className="mt-1 text-sm text-gray-500">
            {meta?.total ?? 0} {readOnly ? 'szkół z uczestnikami w Etapie 2' : 'szkół zarejestrowanych'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => exportStudentsMutation.mutate()}
            disabled={exportStudentsMutation.isPending}
          >
            {exportStudentsMutation.isPending ? 'Generowanie...' : 'Eksport (CSV)'}
          </Button>
          {!readOnly && (
            <Link to="/import">
              <Button variant="secondary">Importuj JSON</Button>
            </Link>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista szkół</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nazwa szkoły</TableHead>
                <TableHead>Adres</TableHead>
                <TableHead>Koordynator</TableHead>
                <TableHead>Uczestnicy</TableHead>
                <TableHead>Zarejestrowano</TableHead>
                {!readOnly && <TableHead className="text-right">Akcje</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {schools.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={readOnly ? 5 : 6} className="py-10 text-center text-gray-500">
                    {readOnly ? (
                      'Brak szkół.'
                    ) : (
                      <>
                        Brak szkół.{' '}
                        <Link to="/import" className="text-primary-600 hover:underline">
                          Importuj plik JSON
                        </Link>
                        , aby dodać szkoły.
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                schools.map((school) => (
                  <>
                    <TableRow
                      key={school.id}
                      className="cursor-pointer"
                      onClick={() => setExpandedId(expandedId === school.id ? null : school.id)}
                    >
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2">
                          <span className={`transition-transform ${expandedId === school.id ? 'rotate-90' : ''}`}>
                            ▶
                          </span>
                          {school.name}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">{school.address}</TableCell>
                      <TableCell>
                        {(school.coordinators?.length ? school.coordinators : school.coordinator ? [school.coordinator] : []).length > 0 ? (
                          <div className="space-y-2">
                            {(school.coordinators?.length ? school.coordinators : school.coordinator ? [school.coordinator] : []).map((coordinator) => (
                              <div key={coordinator.id}>
                                <p className="text-sm font-medium">
                                  {coordinator.firstName} {coordinator.lastName}
                                </p>
                                <p className="text-xs text-gray-500">{coordinator.email}</p>
                                {coordinator.phone ? (
                                  <p className="text-xs text-gray-400">{coordinator.phone}</p>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800">
                          {school._count?.participants ?? 0}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(school.createdAt)}</TableCell>
                      {!readOnly && (
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Czy na pewno chcesz usunąć szkołę "${school.name}" wraz ze wszystkimi uczestnikami?`)) {
                                deleteMutation.mutate(school.id);
                              }
                            }}
                          >
                            Usuń
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>

                    {expandedId === school.id && (
                      <TableRow key={`${school.id}-detail`}>
                        <TableCell colSpan={6} className="bg-gray-50 p-0">
                          {detailLoading ? (
                            <LoadingSpinner size="sm" className="py-4" />
                          ) : schoolDetail?.participants && schoolDetail.participants.length > 0 ? (
                            <div className="p-4">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Uczestnicy ({schoolDetail.participants.length})
                              </p>
                              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                                {schoolDetail.participants.map((p) => (
                                  <div
                                    key={p.id}
                                    className="flex items-center gap-2 rounded bg-white px-3 py-2 text-sm border"
                                  >
                                    <span className="font-medium text-gray-800">
                                      {p.firstName} {p.lastName}
                                    </span>
                                    <span className="text-gray-400">·</span>
                                    <span className="text-gray-500 text-xs truncate">{p.email}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="py-4 text-center text-sm text-gray-500">
                              Brak uczestników dla tej szkoły
                            </p>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Strona {meta.page} z {meta.totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Poprzednia
            </Button>
            <Button variant="secondary" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
              Następna
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
